import styled, { css } from 'styled-components';
import React, { useEffect, useRef } from 'react';
import { useIntersectionObserver } from 'src/hooks/use-intersection-observer';
import { XIcon } from './x-icon';

type TrianglePosition = {
  right?: number;
  left?: number;
  top?: number;
  bottom?: number;
};

type PositionConfig = {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
  width?: number;
  triangle?: TrianglePosition | null;
};

type TooltipProps = {
  children: React.ReactNode;
  position: PositionConfig;
  onClose: () => void;
  onContentClick?: () => void;
  onImpression?: () => void;
  showCloseButton?: boolean;
  maxWidth?: number;
  className?: string;
  dataTestId?: string;
  ariaLabel?: string;
};

export const Tooltip = ({
  children,
  position,
  onClose,
  onContentClick,
  onImpression,
  showCloseButton = true,
  maxWidth = 366,
  className,
  dataTestId = 'tooltip',
  ariaLabel = 'Tooltip content',
}: TooltipProps): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);

  const entry = useIntersectionObserver(ref, {
    threshold: 0.1,
    freezeOnceVisible: true,
  });

  useEffect(() => {
    if (entry?.isIntersecting) {
      onImpression?.();
    }
  }, [entry?.isIntersecting, onImpression]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return (): void => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onContentClick?.();
    }
  };

  const handleClose = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    onClose();
  };

  const containerStyle: React.CSSProperties = {
    ...(position.top !== undefined && { top: `${position.top}px` }),
    ...(position.right !== undefined && { right: `${position.right}px` }),
    ...(position.bottom !== undefined && { bottom: `${position.bottom}px` }),
    ...(position.left !== undefined && { left: `${position.left}px` }),
    ...(position.width !== undefined && { width: `${position.width}px` }),
    ...(position.triangle?.right !== undefined && { '--triangle-right': `${position.triangle.right}px` }),
    ...(position.triangle?.left !== undefined && { '--triangle-left': `${position.triangle.left}px` }),
    ...(position.triangle?.top !== undefined && { '--triangle-top': `${position.triangle.top}px` }),
    ...(position.triangle?.bottom !== undefined && { '--triangle-bottom': `${position.triangle.bottom}px` }),
  };

  const hasClickHandler = !!onContentClick;

  return (
    <TooltipContainer
      ref={ref}
      style={containerStyle}
      data-testid={dataTestId}
      onClick={hasClickHandler ? onContentClick : undefined}
      onKeyDown={hasClickHandler ? handleKeyDown : undefined}
      role={hasClickHandler ? 'button' : 'region'}
      tabIndex={hasClickHandler ? 0 : undefined}
      aria-label={ariaLabel}
      $hasTriangle={position.triangle !== null}
      $maxWidth={maxWidth}
      className={className}
    >
      <HeaderContainer>
        {children}
        {showCloseButton && (
          <CloseButton
            data-testid={`${dataTestId}-close-button`}
            onClick={handleClose}
            aria-label={`Close ${ariaLabel}`}
          >
            <XIcon />
          </CloseButton>
        )}
      </HeaderContainer>
    </TooltipContainer>
  );
};

export const CloseButton = styled.button`
  width: 20px;
  height: 20px;
  cursor: pointer;
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const HeaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme: { spaces } }) => spaces[4]};
  align-self: stretch;
`;

export const TooltipContainer = styled.div<{
  $hasTriangle: boolean;
  $maxWidth: number;
}>`
  ${({ theme: { colors, spaces }, $maxWidth }) => css`
    display: flex;
    max-width: ${$maxWidth}px;
    flex-direction: column;
    align-items: flex-start;
    position: absolute;
    padding: ${spaces[4]};
    border-radius: ${spaces[4]};
    background: ${colors.white};
    box-shadow: 0 6px 20px 0 rgba(0, 0, 0, 0.25);
    z-index: 12;
  `}

  ${({ $hasTriangle, theme: { colors } }) =>
    $hasTriangle &&
    css`
      &::before {
        content: '';
        position: absolute;
        width: 24px;
        height: 24px;
        background: ${colors.white};
        transform: rotate(45deg);
        /* Default: triangle pointing up from top, positioned from right */
        top: -12px;
        right: var(--triangle-right, 0px);
        left: var(--triangle-left, auto);
        bottom: var(--triangle-bottom, auto);
      }

      /* When triangle-bottom is set, position from bottom instead */
      &[style*='--triangle-bottom']::before {
        top: auto;
        bottom: -12px;
      }
    `}
`;
